import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { useImmobilierSearch } from '../hooks/useImmobilierSearch.js'
import { useImmobilierHistory } from '../hooks/useImmobilierHistory.js'
import { useImmobilierSearchStore } from '../stores/immobilierSearchStore.js'
import { claimImmobilier, fetchImmobilierAnnonces } from '../services/api.js'
import ImmobilierSearchBox from '../components/immobilier/ImmobilierSearchBox.jsx'
import ImmobilierEmptyState from '../components/immobilier/ImmobilierEmptyState.jsx'
import ImmobilierLoadingAnimation, { ImmobilierSearchingAnimation } from '../components/immobilier/ImmobilierLoadingAnimation.jsx'
import { ImmobilierPreviewGrid, ImmobilierClaimSelector } from '../components/immobilier/ImmobilierResultsGrid.jsx'
import ImmobilierSearchHistory from '../components/immobilier/ImmobilierSearchHistory.jsx'
import ImmobilierMyAnnonces from '../components/immobilier/ImmobilierMyAnnonces.jsx'

/**
 * @param {boolean} showAcquired  affiche « Mes annonces acquises » en bas de page. Désactivé dans
 *   la page Prospection : les annonces acquises sont dans le CRM (base « Biens immobiliers »).
 */
export default function Immobilier({ showAcquired = true }) {
  const { status, loading, done, error, annonces, total, searchId, pollCount, refreshing, search, reset, refreshAnnonces } = useImmobilierSearch()
  const filters = useImmobilierSearchStore((s) => s.filters)
  const { loading: historyLoading, searches, load: loadHistory } = useImmobilierHistory(true)

  const [showCounter, setShowCounter] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [myAnnoncesKey, setMyAnnoncesKey] = useState(0)
  const [selectedAnnonces, setSelectedAnnonces] = useState([])
  const [relaunchData, setRelaunchData] = useState(null)

  // Quand le scraping est terminé et les annonces récupérées → déclencher le compteur animé
  useEffect(() => {
    if (done && annonces.length > 0 && !showCounter && !showResults) {
      setShowCounter(true)
    }
  }, [done, annonces, showCounter, showResults])

  const handleSearch = (newFilters) => {
    setShowCounter(false)
    setShowResults(false)
    setSelectedAnnonces([])
    search(newFilters)
  }

  const handleCounterComplete = () => {
    setShowCounter(false)
    setShowResults(true)
    loadHistory()
  }

  const handleClaim = async ({ quantity, creditsNeeded }) => {
    if (!searchId) {
      toast.error('Relancez la recherche pour activer l\'acquisition.', { duration: 4000 })
      handleCancel()
      return
    }

    if (
      !confirm(`Acquérir ${quantity} annonce${quantity > 1 ? 's' : ''} ?\n\nCoût : ${creditsNeeded} crédits`)
    ) return

    setClaiming(true)
    try {
      let toClaim = annonces.slice(0, quantity)
      if (quantity > annonces.length) {
        const data = await fetchImmobilierAnnonces({ ...filters, quantity }, searchId)
        toClaim = (data.annonces || []).slice(0, quantity)
      }
      const res = await claimImmobilier(searchId, toClaim)
      toast.success(
        `${res.claimed_count} annonce${res.claimed_count > 1 ? 's' : ''} enregistrée${res.claimed_count > 1 ? 's' : ''} !\nCrédits débités : ${res.credits_deducted}`,
        { duration: 4000 }
      )
      loadHistory()
      setMyAnnoncesKey(k => k + 1)
      handleCancel()
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(`Crédits insuffisants — solde : ${err.response.data.current ?? '?'} / requis : ${err.response.data.required ?? '?'}`)
      } else {
        toast.error(`Erreur : ${err?.response?.data?.error || err?.message}`)
      }
    } finally {
      setClaiming(false)
    }
  }

  const handleCancel = () => {
    reset()
    setShowCounter(false)
    setShowResults(false)
    setSelectedAnnonces([])
  }

  const handleSelectHistory = (historySearch) => {
    toast('Relancez la recherche avec les mêmes critères pour obtenir des annonces à jour.', { icon: '🔄', duration: 4000 })
  }

  const handleRelaunch = (historySearch) => {
    handleCancel()
    setRelaunchData({ ...historySearch })
  }

  const isInitialLoad = historyLoading && searches.length === 0 && status === 'idle'

  return (
    <div className="prospects-view">
      {isInitialLoad ? (
        <div className="empty-state">
          <div className="loading-spinner" style={{ width: 48, height: 48, border: '4px solid rgb(var(--rgb-brand) / 0.1)', borderTop: '4px solid rgb(var(--rgb-brand))', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: 16 }}>Chargement...</p>
        </div>
      ) : (
        <>
          {/* Hero */}
          <div style={{ marginBottom: 28, paddingBottom: 24, borderBottom: '1px solid rgb(var(--rgb-ink) / 0.07)', order: -2 }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: 'var(--color-text)', letterSpacing: '-0.01em' }}>
              Scraping immobilier
            </h2>
            <p style={{ margin: '0 0 18px', fontSize: 14, color: 'rgb(var(--rgb-ink) / 0.45)', lineHeight: 1.6, maxWidth: 560 }}>
              Choisissez une ville et lancez un scraping sur LeBonCoin et PAP. Les résultats arrivent en 10 à 40 minutes selon le nombre de pages.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { icon: '🏠', label: 'Appartements & Maisons' },
                { icon: '🏢', label: 'Bureaux & Locaux' },
                { icon: '🌱', label: 'Terrains' },
                { icon: '💶', label: 'Filtres prix & surface' },
              ].map(({ icon, label }) => (
                <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgb(var(--rgb-ink) / 0.5)', background: 'rgb(var(--rgb-ink) / 0.05)', border: '1px solid rgb(var(--rgb-ink) / 0.09)', borderRadius: 20, padding: '5px 12px' }}>
                  {icon} {label}
                </span>
              ))}
            </div>
          </div>

          {/* Animation de scraping (phases : scraping / polling / fetching) */}
          {(status === 'scraping' || status === 'polling' || status === 'fetching') && (
            <ImmobilierSearchingAnimation status={status} filters={filters} pollCount={pollCount} onCancel={handleCancel} />
          )}

          {/* Compteur animé après récupération des annonces */}
          {showCounter && done && (
            <ImmobilierLoadingAnimation
              targetNumber={total}
              filters={filters}
              onComplete={handleCounterComplete}
            />
          )}

          {status === 'idle' && !showCounter && (
            <ImmobilierSearchBox onSearch={handleSearch} loading={loading} relaunchData={relaunchData} />
          )}

          {/* Erreur */}
          {error && (
            <div className="empty-state">
              <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
              <h3 style={{ color: '#ef4444', margin: '0 0 8px' }}>Erreur</h3>
              <p style={{ color: 'rgb(var(--rgb-ink) / 0.45)', fontSize: 13 }}>{error}</p>
              <button
                onClick={handleCancel}
                style={{ marginTop: 16, padding: '8px 20px', borderRadius: 8, background: 'rgb(var(--rgb-ink) / 0.06)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', color: 'rgb(var(--rgb-ink) / 0.6)', cursor: 'pointer', fontSize: 13 }}
              >
                Réessayer
              </button>
            </div>
          )}

          {/* État vide */}
          {status === 'idle' && !error && !isInitialLoad && <ImmobilierEmptyState />}

          {/* Résultats */}
          {showResults && annonces.length > 0 && (
            <>
              {/* Bandeau PAP background */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, background: 'rgba(255,180,0,0.06)', border: '1px solid rgba(255,180,0,0.2)', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'rgba(255,200,50,0.8)' }}>
                  ⏳ Les téléphones PAP se chargent en arrière-plan (15-20 min). Rafraîchissez pour voir les nouveaux contacts.
                </span>
                <button
                  onClick={refreshAnnonces}
                  disabled={refreshing}
                  style={{ padding: '5px 14px', borderRadius: 6, background: 'rgba(255,180,0,0.12)', border: '1px solid rgba(255,180,0,0.35)', color: 'rgba(255,200,50,0.9)', fontSize: 12, cursor: refreshing ? 'default' : 'pointer', fontWeight: 500, whiteSpace: 'nowrap', opacity: refreshing ? 0.6 : 1 }}
                >
                  {refreshing ? '⏳ Chargement...' : '↻ Rafraîchir les résultats'}
                </button>
              </div>
              <ImmobilierPreviewGrid annonces={annonces} totalResults={total} filters={filters} />
              <ImmobilierClaimSelector
                totalResults={total}
                previewCount={annonces.length}
                annonces={annonces}
                onClaim={handleClaim}
                onCancel={handleCancel}
                disabled={claiming || !searchId}
                noSearchId={!searchId}
              />
            </>
          )}

          {/* Zéro résultat */}
          {showResults && annonces.length === 0 && (
            <div className="empty-state">
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔎</div>
              <h3 style={{ color: 'rgb(var(--rgb-ink) / 0.6)' }}>Aucune annonce trouvée</h3>
              <p style={{ color: 'rgb(var(--rgb-ink) / 0.3)', fontSize: 13 }}>
                Élargissez vos critères (localisation, fourchette de prix, surface…)
              </p>
              <button onClick={handleCancel} style={{ marginTop: 12, padding: '8px 20px', borderRadius: 8, background: 'rgb(var(--rgb-ink) / 0.06)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', color: 'rgb(var(--rgb-ink) / 0.6)', cursor: 'pointer', fontSize: 13 }}>
                Nouvelle recherche
              </button>
            </div>
          )}

          <ImmobilierSearchHistory
            searches={searches}
            loading={historyLoading}
            onSelectSearch={handleSelectHistory}
            onRelaunch={handleRelaunch}
          />

          {showAcquired && <ImmobilierMyAnnonces refreshKey={myAnnoncesKey} />}
        </>
      )}
    </div>
  )
}
